import React, { useState, useEffect } from "react";
import { getRules, updateRules } from "../api/authApi";
import RulesPanel from "../components/RulesPanel";
import { DETECTION_RULES } from "../data/DetectionRules";
import { useAuth } from "../context/AuthContext";

// Mapping: Display name => column key
const RULES_MAP = {
  "Too high risk level": "rule_high_risk",
  "Country blocked": "rule_country_blocked",
  "Early Fraud Warning Detection": "rule_efw",
  "One trial per device/IP/email": "rule_ip",
  "Block disposable or low-reputation emails": "rule_email",
  "Detect promo/referral abuse": "rule_promo_abuse",
  "Scraping & throttling detection": "rule_scraping",
  "Account farming & refund flagging": "rule_farming",
  "Signup Velocity Analysis": "rule_signup_velocity",
  "Promo Code Use Velocity": "rule_promo_velocity",
  "Behavioral Entropy Score": "rule_entropy",
  "Timezone Mismatch Detection": "rule_timezone_mismatch",
  "Referrer & Origin Mismatch": "rule_referrer_mismatch",
  "Credit Card Reuse Fingerprint": "rule_cc_reuse",
  "Device Fingerprint Reuse": "rule_device_fingerprint_reuse",
};
const ALL_RULE_KEYS = Object.values(RULES_MAP);

const DEFAULT_DROPDOWN_VALUES = {
  risk_level_threshold: 70,
};

export default function RulesPage() {
  const [rules, setRules] = useState({
    ...Object.fromEntries(ALL_RULE_KEYS.map((k) => [k, false])),
    ...DEFAULT_DROPDOWN_VALUES,
  });
  const [loading, setLoading] = useState(true);

  const { user, loading: authLoading } = useAuth();

  useEffect(() => {
    if (authLoading || !user) return;
    getRules()
      .then((res) => {
        // Fill missing dropdowns with defaults
        const newRules = { ...rules, ...res.data };
        Object.keys(DEFAULT_DROPDOWN_VALUES).forEach((k) => {
          if (typeof newRules[k] !== "number") {
            newRules[k] = DEFAULT_DROPDOWN_VALUES[k];
          }
        });
        setRules(newRules);
        setLoading(false);
      })
      .catch(() => setLoading(false));
    // eslint-disable-next-line
  }, [authLoading, user]);

  const allRuleNames = DETECTION_RULES.flatMap((g) => g.items.map((item) => item.name));

  const handleRuleToggle = (idx) => {
    const ruleName = allRuleNames[idx];
    const ruleKey = RULES_MAP[ruleName];
    const updated = { ...rules, [ruleKey]: !rules[ruleKey] };
    setRules(updated);
    updateRules(updated);
  };

  // Universal handler for dropdowns (can expand with more keys in the future)
  const handleDropdownChange = (key, value) => {
    const updated = { ...rules, [key]: value };
    setRules(updated);
    updateRules(updated);
  };

  if (loading || authLoading) return <div>Loading…</div>;
  if (!user) return <div>Please log in.</div>;

  const toggles = allRuleNames.map((name) => rules[RULES_MAP[name]]);
  // Collect dropdownValues (for all keys in DEFAULT_DROPDOWN_VALUES, or extend with more)
  const dropdownValues = {};
  Object.keys(DEFAULT_DROPDOWN_VALUES).forEach((k) => {
    dropdownValues[k] = rules[k];
  });

  return (
    <RulesPanel
      toggles={toggles}
      onToggle={handleRuleToggle}
      dropdownValues={dropdownValues}
      onDropdownChange={handleDropdownChange}
    />
  );
}
